import EventNames from '../../../config/eventNames';
import { ContainerPluginFacade, JWebErrorHandler } from '../../JWeb';
import { SubscriptionHandle } from '../../JWeb/types';
import * as T from '../types';
import isObject from '../../../utils/isObject';
import { nativeEventPublisherPromise, nativeSubscriberPromise } from './utils';
import bindAllMethods from '../../../utils/bindAllMethods';
import { publisherId } from '../utils';
import BaseEventService from '../BaseEventService';
import type { EventServiceInputType } from '../../../infra/startup/types';
import { internalLogger } from '../../../interface/v1/clientOsLogger';
class NativeEventService extends BaseEventService {
  private eventsToInjectFilterByContainerInstanceId: Record<string, boolean> =
    {};
  private jWebContainerInstanceId;

  constructor(eventServiceProps: EventServiceInputType) {
    super();
    if (!eventServiceProps?.removeSelfFilterByContainerInstanceId) {
      Object.values(EventNames).forEach((eventName) => {
        this.eventsToInjectFilterByContainerInstanceId[eventName] = true;
      });
    }
    bindAllMethods(this);
  }

  async init(): Promise<void> {
    this.jWebContainerInstanceId =
      (await ContainerPluginFacade?.getJWebContainerInstanceId()) || 'qwe';
    this._markAsReady();
  }

  async subscribe(
    eventName: string,
    action: T.EventServiceCallBackType,
    options?: T.EventServiceSubscribeOptionsType
  ): Promise<T.ListenerHandlerType> {
    const nativeSubscriber = await nativeSubscriberPromise;

    const nativeListenerHandler = JWebErrorHandler<SubscriptionHandle>(
      await nativeSubscriber?.subscribe(
        { eventName, publisherId: options?.publisherId },
        ({ eventData, publisherId: nativePublisherId }) => {
          const filterByContainerInstanceId = (eventData as any)
            ?.filterByContainerInstanceId;

          const allowedToCallMethod = (() => {
            if (typeof filterByContainerInstanceId === 'string') {
              return (
                filterByContainerInstanceId === this.jWebContainerInstanceId
              );
            } else {
              return true;
            }
          })();

          if (allowedToCallMethod) {
            action({
              eventData,
              eventName,
              publisherId: nativePublisherId || publisherId
            });
          }
        }
      )
    );

    return {
      unsubscribe: async () => {
        await nativeListenerHandler?.unsubscribe();
      }
    };
  }

  async publish(
    eventName: string,
    eventData: T.EventServiceValueType['eventData']
  ): Promise<void> {
    const sanitizedEventData = isObject(eventData)
      ? {
          ...eventData
        }
      : {};
    const nativeEventPublisher = await nativeEventPublisherPromise;

    if (
      this.eventsToInjectFilterByContainerInstanceId[eventName] &&
      sanitizedEventData.filterByContainerInstanceId === undefined
    ) {
      sanitizedEventData.filterByContainerInstanceId =
        this.jWebContainerInstanceId;
    }

    internalLogger?.debug('Publishing into channel: ', eventName);
    await nativeEventPublisher?.publish(eventName, sanitizedEventData);
  }

  addEventListener(name: string, action: T.EventCallBackType): void {
    super.addEventListener(name, action);
  }

  removeEventListener(name: string, action: T.EventCallBackType): void {
    super.removeEventListener(name, action);
  }
}

export default NativeEventService;
